/**
 * @file resolveAttachmentSyncNudge.ts
 * @description The sentence suggesting Syncretic File Sync for one large file,
 * or null when it must not be shown. Consumer: `AttachmentSyncNudge`.
 *
 * Every condition is a reason to stay silent, which is why they are gathered
 * here rather than spread through a render. A person who already keeps
 * something has it installed; a person who dismissed this has answered; a file
 * under `LARGE_ATTACHMENT_ADVISORY_BYTES` is one the browser uploads perfectly
 * well; and with no configured download URL there is nowhere to send anyone, so
 * the line would be advice with no next step.
 *
 * The threshold is the shared advisory constant the upload path already reads.
 * It is advisory: nothing here refuses anything.
 */

import { LARGE_ATTACHMENT_ADVISORY_BYTES } from '#types';
import { formatBytes } from './formatBytes';

export type AttachmentSyncNudgeRequest = {
    /** The file's current size. Absent for a row whose version carries none. */
    sizeBytes: number | undefined;
    /** Whether the signed-in user keeps anything at all, anywhere. */
    hasAnyPin: boolean;
    /** True while the user document is unread, when neither answer is known yet. */
    loading: boolean;
    /** Whether `UserSettings.fileSyncNudgeDismissedAt` is set. */
    dismissed: boolean;
    /** The organization's configured download page, or null when none is set. */
    downloadUrl: string | null;
};

export const resolveAttachmentSyncNudge = ({
    sizeBytes,
    hasAnyPin,
    loading,
    dismissed,
    downloadUrl,
}: AttachmentSyncNudgeRequest): string | null => {
    if (typeof sizeBytes !== 'number' || sizeBytes <= LARGE_ATTACHMENT_ADVISORY_BYTES) {
        return null;
    }
    if (loading || hasAnyPin || dismissed || downloadUrl === null) {
        return null;
    }

    return `This file is ${formatBytes(sizeBytes)}. Syncretic File Sync keeps files this large on your computer and sends changes in the background.`;
};
